import React from 'react';
import { useT } from '../i18n/LanguageContext';

const CGV = () => {
  const { lang } = useT();

  const content = {
    fr: {
      badge: 'GPE – ÉNERGIES & SERVICES',
      title: 'Conditions générales de vente',
      updated: 'Dernière mise à jour : Avril 2026',
      intro: 'Les présentes conditions générales régissent les relations contractuelles entre GPE Énergies & Services et ses clients dans le cadre des prestations d’installation électrique, d’énergies renouvelables, de CVC, de VRD et de bureau d’études.',
      sections: [
        {
          title: 'Devis & commande',
          text: 'Toute prestation fait l’objet d’un devis détaillé. La validation du devis constitue l’acceptation des présentes conditions générales.',
        },
        {
          title: 'Délais d’exécution',
          text: 'Les délais communiqués sont indicatifs et dépendent notamment de la disponibilité des conditions nécessaires à l’intervention, de l’accès au site et de la fourniture des éléments contractuels.',
        },
        {
          title: 'Tarifs & règlement',
          text: 'Les prix sont exprimés hors taxes. Les modalités de règlement sont précisées dans chaque devis. Tout retard de paiement peut entraîner l’application de pénalités conformément au taux légal en vigueur.',
        },
        {
          title: 'Garantie',
          text: 'Les installations bénéficient des garanties légales applicables, notamment les garanties de parfait achèvement, biennale et décennale lorsque la nature des travaux les rend applicables.',
        },
        {
          title: 'Litiges',
          text: 'En l’absence de résolution amiable, les litiges relèvent de la compétence des tribunaux du ressort du siège social, conformément aux dispositions applicables.',
        },
      ],
    },

    en: {
      badge: 'GPE – ENERGY & SERVICES',
      title: 'General Terms and Conditions of Sale',
      updated: 'Last updated: April 2026',
      intro: 'These general terms and conditions govern the contractual relationship between GPE Energy & Services and its clients for electrical installation, renewable energy, HVAC, VRD and engineering services.',
      sections: [
        {
          title: 'Quotation & Order',
          text: 'Each service is subject to a detailed quotation. Approval of the quotation constitutes acceptance of these general terms and conditions.',
        },
        {
          title: 'Execution Times',
          text: 'The stated execution times are indicative and depend in particular on the conditions required for the work, site access and provision of the necessary contractual elements.',
        },
        {
          title: 'Prices & Payment',
          text: 'Prices are stated excluding taxes. Payment terms are specified in each quotation. Late payment may result in penalties in accordance with the applicable legal rate.',
        },
        {
          title: 'Warranty',
          text: 'Installations benefit from the applicable statutory warranties, including completion, two-year and ten-year warranties where applicable.',
        },
        {
          title: 'Disputes',
          text: 'In the absence of an amicable settlement, disputes shall fall under the jurisdiction of the courts within the jurisdiction of the company’s registered office, in accordance with applicable law.',
        },
      ],
    },

    ar: {
      badge: 'GPE – الطاقة والخدمات',
      title: 'الشروط والأحكام العامة للبيع',
      updated: 'آخر تحديث: أبريل 2026',
      intro: 'تنظم هذه الشروط والأحكام العامة العلاقة التعاقدية بين GPE للطاقة والخدمات وعملائها فيما يتعلق بخدمات التركيبات الكهربائية والطاقة المتجددة والتدفئة والتكييف وشبكات الطرق والمرافق ومكتب الدراسات.',
      sections: [
        {
          title: 'عرض السعر والطلب',
          text: 'تخضع كل خدمة لعرض سعر مفصل، ويُعد اعتماد عرض السعر قبولاً بهذه الشروط والأحكام العامة.',
        },
        {
          title: 'مواعيد التنفيذ',
          text: 'المواعيد المحددة للتنفيذ إرشادية وتعتمد بشكل خاص على توفر الظروف اللازمة لتنفيذ الأعمال وإمكانية الوصول إلى الموقع وتوفير العناصر التعاقدية المطلوبة.',
        },
        {
          title: 'الأسعار والدفع',
          text: 'الأسعار المذكورة لا تشمل الضرائب. يتم تحديد شروط الدفع في كل عرض سعر. وقد يؤدي التأخر في الدفع إلى تطبيق غرامات وفقاً للنسبة القانونية المعمول بها.',
        },
        {
          title: 'الضمان',
          text: 'تستفيد التركيبات من الضمانات القانونية المعمول بها، بما في ذلك ضمان إتمام الأعمال والضمان لمدة سنتين والضمان لمدة عشر سنوات عندما تكون طبيعة الأعمال خاضعة لهذه الضمانات.',
        },
        {
          title: 'النزاعات',
          text: 'في حال عدم التوصل إلى تسوية ودية، تخضع النزاعات لاختصاص المحاكم التابعة لمقر الشركة وفقاً للقوانين والأنظمة المعمول بها.',
        },
      ],
    },
  };

  const c = content[lang] || content.fr;
  const isArabic = lang === 'ar';

  return (
    <main className="bg-white min-h-screen">

      <section className="bg-[#0b1c3a] text-white pt-40 pb-20">
        <div className="max-w-6xl mx-auto px-6">

          <span className="text-[#f5c518] text-sm font-bold tracking-[0.2em]">
            {c.badge}
          </span>

          <h1 className="text-4xl md:text-5xl font-bold mt-4">
            {c.title}
          </h1>

          <p className="mt-4 text-white/70">
            {c.updated}
          </p>

        </div>
      </section>

      <section
        className="max-w-5xl mx-auto px-6 py-16 text-slate-700"
        dir={isArabic ? 'rtl' : 'ltr'}
      >

        <p className="text-base leading-8">
          {c.intro}
        </p>

        {c.sections.map((section, index) => (
          <section key={index} className="mt-12">

            <h2 className="text-2xl font-bold text-[#0b1c3a] mb-4">
              {section.title}
            </h2>

            <p className="text-base leading-8">
              {section.text}
            </p>

          </section>
        ))}

      </section>

    </main>
  );
};

export default CGV;
